<script>
    import { onMount, onDestroy } from 'svelte'
    import FileService from '../../helpers/FileService'
    import UploadService from '../../helpers/UploadService'
    import HypeTrainService from '../../helpers/HypeTrainService'
    import HypeConfigService from '../../helpers/HypeConfigService'

    import { configStore } from '../../stores/configStore'
    import { mediaStore } from '../../stores/mediaStore'
    import MediaCard from '../../components/MediaCard.svelte'
    import FileUploader from '../../components/FileUploader.svelte'
    import { get } from 'svelte/store'
    import { addMinutes } from 'date-fns'

    let hypeConfigData = {}
    let files = {}
    let trainLevels = [1, 2, 3, 4, 5]

    configStore.subscribe((store) => {
        hypeConfigData = store.media
    })

    onMount(async () => {
        await FileService.get()
        await HypeConfigService.get()
    })

    async function handleClick() {
        const data = { media: hypeConfigData }
        await HypeConfigService.create(data)
    }

    async function uploadFiles() {
        let promises = []
        Object.values(files).forEach((file: Blob) => {
            const formData = new FormData()
            formData.append('uri', file)
            const promise = UploadService.upload(formData)
                .then((res) => {
                    console.log('upload completed')
                })
                .catch((err) => {
                    console.error(err)
                })
            promises.push(promise)
        })
        return Promise.all(promises)
    }

    async function removeFile(id) {
        await UploadService.remove(id)
            .then((res) => {
                console.log('deletion completed')
            })
            .catch((err) => {
                console.error(err)
            })
    }

    async function testEmit(index) {
        console.log('testing level: ', index + 1)
        const config = hypeConfigData[index]
        const medias = get(mediaStore) as Array<any>
        const media = medias.find((el) => el.id === config.name)
        const expiry = addMinutes(new Date(), 5).toISOString()
        const data = {
            testEvent: true,
            url: media.url,
            level: index + 1,
            loop: config.loop,
            expiry,
        }
        console.log(config, media, expiry, data)
        return await HypeTrainService.testEvent(data)
    }
</script>

<div class="flex flex-col">
    <div class="pb-2 border-b border-gray-200">
        <h3 class="text-lg leading-6 font-medium text-gray-900">
            Media Uploader
        </h3>
    </div>

    <div class="mt-2">
        <h2 class="text-sm leading-4 font-light text-cool-gray-900 mb-4">
            Upload any media you want to use in your hype train events
        </h2>
    </div>
    <div class="flex-1">
        <FileUploader {files} {uploadFiles} />
    </div>
</div>

<div class="flex flex-col mt-8">
    <div class="pb-2 border-b border-gray-200">
        <h3 class="text-lg leading-6 font-medium text-gray-900">Your Media</h3>
    </div>

    <div class="mt-2">
        <h2 class="text-sm leading-4 font-light text-cool-gray-900 mb-4">
            Media that is available to choose from, uploaded media will appear
            here when processing has completed
        </h2>
    </div>
    <ul
        class="mt-3 grid grid-cols-1 gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {#each $mediaStore as media}
            <MediaCard {media} remove={removeFile} />
        {/each}
    </ul>
</div>

<div class="mt-8 pb-2 border-b border-gray-200">
    <h3 class="text-lg leading-6 font-medium text-gray-900">Level Config</h3>
</div>

<div class="mt-2">
    <h2 class="text-sm leading-4 font-light text-cool-gray-900 mb-4">
        Select which media to play for each level
    </h2>
</div>
<div class="mt-5 md:mt-0 md:col-span-2">
    <div class="shadow sm:rounded-md sm:overflow-hidden">
        {#each trainLevels as level, index}
            <div
                class="{index % 2 ? 'bg-gray-150' : 'bg-gray-50'} px-8 py-3 sm:grid sm:grid-cols-2 sm:items-start">
                <label
                    for="media-display"
                    class="block col-span-1 text-sm leading-8 font-medium text-gray-700">
                    Level
                    {index + 1}
                </label>
                <div class="flex flex-row justify-end">
                    <div
                        class="flex flex-row mr-10 align-middle place-items-center">
                        <label
                            for="loop-toggle"
                            class="block mr-2 text-sm leading-8 font-light text-gray-700">
                            Loop
                        </label>
                        <span
                            on:click={() => {
                                hypeConfigData[index].loop = !hypeConfigData[index].loop
                            }}
                            name="loop-toggle"
                            role="checkbox"
                            tabindex="0"
                            aria-checked={hypeConfigData[index].loop}
                            class:background={hypeConfigData[index].loop}
                            class="background-default relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-gray-200 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:shadow-outline">
                            <span
                                aria-hidden="true"
                                class:inner-toggle-button={hypeConfigData[index].loop}
                                class="translate-x-0 relative inline-block h-5 w-5 rounded-full bg-white shadow transform transition ease-in-out duration-200">
                                <span
                                    class:toggle-inner={hypeConfigData[index].loop}
                                    class="opacity-100 ease-in duration-200 absolute inset-0 h-full w-full flex items-center justify-center transition-opacity">
                                    <svg
                                        class="h-3 w-3 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 12 12">
                                        <path
                                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round" />
                                    </svg>
                                </span>
                                <span
                                    class:toggle-outer={hypeConfigData[index].loop}
                                    class="opacity-0 ease-out duration-100 absolute inset-0 h-full w-full flex items-center justify-center transition-opacity">
                                    <svg
                                        class="h-3 w-3 text-orange-600"
                                        fill="currentColor"
                                        viewBox="0 0 12 12">
                                        <path
                                            d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                                    </svg>
                                </span>
                            </span>
                        </span>
                    </div>
                    <div
                        class="flex flex-row h-full mr-5 align-middle place-items-center">
                        <label
                            for="media-display"
                            class="block mr-2 text-sm leading-8 font-light text-gray-700">
                            Media
                        </label>
                        <div class="max-w-lg rounded-md shadow-sm sm:max-w-xs">
                            <select
                                name="media-display"
                                bind:value={hypeConfigData[index].name}
                                class="form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-orange focus:border-orange-300 sm:text-sm sm:leading-5">
                                <option value={''} hidden disabled selected>
                                    Select Media
                                </option>
                                {#each $mediaStore as media}
                                    <option value={media.id}>{media.id}</option>
                                {/each}
                            </select>
                        </div>
                    </div>
                    <div class="flex flex-row justify-end">
                        <button
                            class="inline-flex justify-center mr-3 py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange active:bg-orange-700 transition duration-150 ease-in-out"
                            on:click={() => {
                                testEmit(index)
                            }}>Test</button>
                    </div>
                </div>
            </div>
        {/each}
        <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
            <span class="inline-flex rounded-md shadow-sm">
                <button
                    type="button"
                    on:click={handleClick}
                    class="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange active:bg-orange-700 transition duration-150 ease-in-out">
                    Save Config
                </button>
            </span>
        </div>
    </div>
</div>

<style lang="postcss">
    .translate {
        @apply translate-x-5;
    }

    .background-default {
        @apply bg-gray-200;
    }

    .background {
        @apply bg-orange-600;
    }

    .inner-toggle-button {
        @apply translate-x-5;
    }

    .toggle-inner {
        @apply opacity-0 ease-out duration-100;
    }

    .toggle-outer {
        @apply opacity-100 ease-in duration-200;
    }
</style>
